import React, { useState, useEffect } from "react";
import useRentalPartnersLogic from "./useRentalPartnersLogic";
import { 
    UserCircleIcon, PhoneIcon, EnvelopeIcon, StarIcon, TruckIcon, 
    MapPinIcon, ArrowLeftIcon, MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon,
    CalendarIcon, DocumentTextIcon 
} from "@heroicons/react/24/outline";

export default function RentalPartnersPage() {
  const {
    partners,
    loading,
    selectedPartner,
    // partnerVehicles, // Not primary anymore
    partnerFleets,
    fetchPartnerVehicles,
    closeVehicleModal,
  } = useRentalPartnersLogic();

  const [selectedFleet, setSelectedFleet] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8; // Number of items per page for the fleet details view

  // Reset pagination and search when changing selection
  useEffect(() => {
      setSearchQuery("");
      setCurrentPage(1);
  }, [selectedPartner, selectedFleet]);

  const getDisplayedVehicles = () => {
      if (!selectedFleet || !selectedFleet.vehicles) return [];
      let vehicles = selectedFleet.vehicles;
      if (searchQuery) {
          const q = searchQuery.toLowerCase();
          vehicles = vehicles.filter(v => 
              v.brand?.name?.toLowerCase().includes(q) ||
              v.model?.name?.toLowerCase().includes(q) ||
              v.plate?.toLowerCase().includes(q) ||
              v.color?.name?.toLowerCase().includes(q)
          );
      }
      return vehicles;
  };
  
  // Pagination Logic
  const currentList = getDisplayedVehicles();
  const totalPages = Math.ceil(currentList.length / itemsPerPage);
  const paginatedList = currentList.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);


  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="mb-8">
        <p className="mt-2 text-sm text-gray-500">Manage and view your verified partners and their fleets.</p>
      </div>
      
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {partners.map((partner) => (
          <div
            key={partner.id}
            onClick={() => fetchPartnerVehicles(partner)}
            className="group relative bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-xl hover:border-blue-500 transition-all duration-300 cursor-pointer overflow-hidden flex flex-col"
          >
            {/* Card Header / Banner (subtle background) */}
            <div className="h-20 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-100 group-hover:from-blue-50 group-hover:to-white transition-colors duration-300"></div>

            <div className="px-6 flex-1 flex flex-col relative">
                {/* Avatar positioned over banner */}
               <div className="-mt-10 mb-4 flex justify-between items-end">
                  <div className="relative">
                    {partner.avatar ? (
                        <img
                        className="w-20 h-20 bg-white rounded-full object-cover border-4 border-white shadow-md"
                        src={partner.avatar.data?.full_url || `${process.env.REACT_APP_API_URL}/assets/${partner.avatar}`}
                        alt=""
                        />
                    ) : (
                        <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center border-4 border-white shadow-md text-gray-300">
                             <UserCircleIcon className="w-16 h-16" />
                        </div>
                    )}
                     <span className="absolute bottom-1 right-1 block h-4 w-4 rounded-full ring-2 ring-white bg-green-400"></span>
                  </div>
                  
                  {/* Rating Badge */}
                  <div className="bg-white px-3 py-1 rounded-full shadow-sm border border-gray-100 flex items-center mb-2">
                       <StarIcon className="w-4 h-4 text-yellow-400 mr-1" fill="currentColor" />
                       <span className="text-sm font-bold text-gray-800">{partner.rating ? Number(partner.rating).toFixed(1) : "N/A"}</span>
                  </div>
               </div>

              <div className="text-left mb-6">
                <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                  {partner.first_name} {partner.last_name}
                </h3>
                 <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mt-1">Authorized Partner</p>
              </div>

               {/* Stats / Quick Info */}
               <div className="grid grid-cols-2 gap-4 mb-6 border-t border-b border-gray-50 py-4">
                    <div className="text-center">
                        <span className="block text-2xl font-bold text-gray-800">
                             {/* Display Fleet Count instead if appropriate, or total vehicles */}
                             {partner.vehicles?.length || (partner.fleets?.reduce((acc,f) => acc + (f.vehicles?.length||0), 0)) || 0}
                        </span>
                         <span className="text-xs text-gray-500 font-medium uppercase">Vehicles</span>
                    </div>
                    <div className="text-center border-l border-gray-100">
                        <span className="block text-2xl font-bold text-gray-800">{partner.rating ? "A" : "-"}</span> {/* Example grade */}
                         <span className="text-xs text-gray-500 font-medium uppercase">Tier</span>
                    </div>
               </div>


              <div className="space-y-3 pb-6 mt-auto">
                 <div className="flex items-center text-sm text-gray-600 space-x-3 bg-gray-50 p-2 rounded-lg group-hover:bg-white transition-colors">
                     <EnvelopeIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                     <span className="truncate">{partner.email}</span>
                 </div>
                 <div className="flex items-center text-sm text-gray-600 space-x-3 bg-gray-50 p-2 rounded-lg group-hover:bg-white transition-colors">
                     <PhoneIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                     <span className="truncate">{partner.phone_number || partner.phone || "No Phone"}</span>
                 </div>
              </div>

            </div>
            
            {/* Action Bar */}
             <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex justify-between items-center group-hover:bg-blue-50 transition-colors duration-300">
                 <span className="text-xs font-semibold text-gray-500 uppercase flex items-center">
                    View Fleets <span className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity transform translate-x-1">→</span>
                 </span>
                 <TruckIcon className="w-5 h-5 text-gray-300 group-hover:text-blue-500 transition-colors" />
             </div>
          </div>
        ))}
      </div>

        {/* Modal / Slideover */}
        {selectedPartner && (
        <div className="fixed inset-0 overflow-y-auto z-50" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity backdrop-blur-sm" aria-hidden="true" onClick={closeVehicleModal}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-2xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full sm:p-8">
              <div className="sm:flex sm:items-start flex-col w-full">
                  
                  {/* Modal Header */}
                  <div className="flex justify-between items-center w-full mb-6 border-b border-gray-100 pb-4">
                    <div className="flex items-center">
                         {selectedFleet && (
                             <button onClick={() => setSelectedFleet(null)} className="mr-4 p-2 rounded-full hover:bg-gray-100 text-gray-500 transition-colors">
                                 <ArrowLeftIcon className="w-5 h-5" />
                             </button>
                         )}
                        <div>
                            <h3 className="text-2xl leading-6 font-bold text-gray-900" id="modal-title">
                                {selectedFleet ? `Fleet: ${selectedFleet.name || 'Unnamed Fleet'}` : 'Partner Fleets'}
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">
                                {selectedFleet 
                                    ? `Showing details for this location`
                                    : <span>Managing fleets for <span className="font-semibold text-blue-600">{selectedPartner.first_name} {selectedPartner.last_name}</span></span>
                                }
                            </p>
                        </div>
                    </div>
                    <button onClick={closeVehicleModal} className="text-gray-400 hover:text-gray-500 bg-gray-100 hover:bg-gray-200 p-2 rounded-full transition-colors">
                        <span className="sr-only">Close</span>
                        <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                  </div>

                  {/* Modal Body */}
                  <div className="w-full min-h-[400px]">
                      
                      {/* VIEW 1: FLEET LIST */}
                      {!selectedFleet && (
                          partnerFleets && partnerFleets.length > 0 ? (
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                                {partnerFleets.map(fleet => (
                                    <div 
                                        key={fleet.id} 
                                        onClick={() => setSelectedFleet(fleet)}
                                        className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg hover:border-blue-500 transition-all cursor-pointer group"
                                    >
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="p-3 bg-blue-50 rounded-lg group-hover:bg-blue-100 text-blue-600 transition-colors">
                                                <MapPinIcon className="w-6 h-6" />
                                            </div>
                                            <span className="text-xs font-bold bg-gray-100 px-2 py-1 rounded text-gray-600">
                                                ID: {fleet.id}
                                            </span>
                                        </div>
                                        <h4 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">
                                            {fleet.name || "Fleet Location"}
                                        </h4>
                                        <p className="text-sm text-gray-500 truncate mb-6">
                                            {fleet.address || "No Address Provided"}
                                        </p>
                                        
                                        <div className="flex items-center pt-4 border-t border-gray-50 space-x-2">
                                            <div className="flex items-center text-gray-700">
                                                <TruckIcon className="w-4 h-4 mr-2 text-gray-400" />
                                                <span className="font-bold">{fleet.vehicles?.length || 0}</span>
                                                <span className="text-xs text-gray-500 ml-1">Cars</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                          ) : (
                              <div className="text-center py-20 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center h-full">
                                  <MapPinIcon className="h-16 w-16 text-gray-300 mb-4" />
                                  <h3 className="text-lg font-medium text-gray-900">No Fleets Found</h3>
                                  <p className="text-gray-500 mt-2">This partner has no assigned fleet locations.</p>
                              </div>
                          )
                      )}

                      {/* VIEW 2: DETAILS (VEHICLES OR RESERVATIONS) */}
                      {selectedFleet && (
                          <>
                            {/* Toolbar: Search + Info */}
                            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 space-y-4 sm:space-y-0">
                                <div className="text-sm text-gray-500">
                                    Showing <span className="font-bold text-gray-900">{paginatedList.length}</span> of <span className="font-bold text-gray-900">{currentList.length}</span> vehicles
                                </div>
                                <div className="relative w-full sm:w-72">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                    </div>
                                    <input
                                        type="text"
                                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition duration-150 ease-in-out"
                                        placeholder="Search vehicles..."
                                        value={searchQuery}
                                        onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                                    />
                                </div>
                            </div>
                            
                            {/* List Content */}
                             {paginatedList.length > 0 ? (
                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                    {paginatedList.map(vehicle => (
                                        <div key={vehicle.id} className="group border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow duration-300 bg-white">
                                            <div className="relative h-40 bg-gray-100 overflow-hidden">
                                                {vehicle.image ? (
                                                    <img 
                                                        src={`${process.env.REACT_APP_API_URL}/assets/${vehicle.image.id || vehicle.image}`} 
                                                        alt={vehicle.model?.name} 
                                                        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                                                        <TruckIcon className="w-12 h-12 opacity-20" />
                                                    </div>
                                                )}
                                                <div className="absolute top-2 right-2 bg-white/90 backdrop-blur px-2 py-0.5 rounded text-xs font-bold text-gray-700 shadow-sm">
                                                    {vehicle.year}
                                                </div>
                                            </div>
                                            <div className="p-4">
                                                <div className="mb-2">
                                                    <h4 className="font-bold text-gray-900 truncate" title={`${vehicle.brand?.name} ${vehicle.model?.name}`}>{vehicle.brand?.name} {vehicle.model?.name}</h4>
                                                    <p className="text-xs text-gray-500">{vehicle.color?.name || 'Unknown Color'}</p>
                                                </div>
                                                
                                                <div className="flex items-center justify-between mt-4">
                                                    <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded text-gray-600 border border-gray-200">{vehicle.plate}</span>
                                                    <span className={`h-2.5 w-2.5 rounded-full ${vehicle.status === 'Available' ? 'bg-green-500' : 'bg-gray-300'}`} title={vehicle.status || 'Unknown'}></span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                                    <TruckIcon className="mx-auto h-12 w-12 text-gray-300" />
                                    <h3 className="mt-2 text-sm font-medium text-gray-900">No vehicles found</h3>
                                </div>
                            )}


                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-6">
                                    <div className="flex flex-1 justify-between sm:hidden">
                                        <button
                                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                            disabled={currentPage === 1}
                                            className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                        >
                                            Previous
                                        </button>
                                        <button
                                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                            disabled={currentPage === totalPages}
                                            className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                        >
                                            Next
                                        </button>
                                    </div>
                                    <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                                        <div>
                                            <p className="text-sm text-gray-700">
                                                Page <span className="font-medium">{currentPage}</span> of <span className="font-medium">{totalPages}</span>
                                            </p>
                                        </div>
                                        <div>
                                            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                                                <button
                                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                                    disabled={currentPage === 1}
                                                    className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                                                >
                                                    <span className="sr-only">Previous</span>
                                                    <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                                                </button>
                                                {(() => {
                                                    const pageNumbers = [];
                                                    const maxVisible = 5; 
                                                    
                                                    if (totalPages <= 7) {
                                                        for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
                                                    } else {
                                                        if (currentPage <= 4) {
                                                            for (let i = 1; i <= 5; i++) pageNumbers.push(i);
                                                            pageNumbers.push('...');
                                                            pageNumbers.push(totalPages);
                                                        } else if (currentPage >= totalPages - 3) {
                                                            pageNumbers.push(1);
                                                            pageNumbers.push('...');
                                                            for (let i = totalPages - 4; i <= totalPages; i++) pageNumbers.push(i);
                                                        } else {
                                                            pageNumbers.push(1);
                                                            pageNumbers.push('...');
                                                            for (let i = currentPage - 1; i <= currentPage + 1; i++) pageNumbers.push(i);
                                                            pageNumbers.push('...');
                                                            pageNumbers.push(totalPages);
                                                        }
                                                    }

                                                    return pageNumbers.map((page, index) => (
                                                        <React.Fragment key={index}>
                                                            {page === '...' ? (
                                                                <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 focus:outline-offset-0">
                                                                    ...
                                                                </span>
                                                            ) : (
                                                                <button
                                                                    onClick={() => setCurrentPage(page)}
                                                                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${currentPage === page ? 'bg-blue-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600' : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'}`}
                                                                >
                                                                    {page}
                                                                </button>
                                                            )}
                                                        </React.Fragment>
                                                    ));
                                                })()}
                                                <button
                                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                                    disabled={currentPage === totalPages}
                                                    className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                                                >
                                                    <span className="sr-only">Next</span>
                                                    <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                                                </button>
                                            </nav>
                                        </div>
                                    </div>
                                </div>
                            )}
                          </>
                      )}
                  </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
